import { JobTitle } from '@accredible-frontend-v2/models';
import { AccredibleApiService } from '@accredible-frontend-v2/services/api';
import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  CareerPreference,
  CareerPreferenceRequest,
  CredentialJobInsights,
  JobInsight,
  JobInsightsPaginatedResponse,
  JobRolePreferenceRequest,
  JobRolePreferenceStatus,
  SuggestedGroup,
} from '../../entities/insights/job-insight.model';

@Injectable({
  providedIn: 'root',
})
export class JobInsightsApiService extends AccredibleApiService {
  private readonly API_BASE_URL = '/v1/recipient';
  private readonly CREDENTIAL_NET_API_BASE_URL = '/v1/credential-net';
  private readonly INTERNAL_API_BASE_URL = '/v1/internal/job_insights';

  loadJobInsight(id: number): Observable<JobInsight> {
    const url = `${this.API_BASE_URL}/users/job_role_insights/${id}`;
    return this._get(url).pipe(
      map((res) => this._handleResponse(res)),
      catchError((res) => this._handleError(res)),
    );
  }

  loadJobInsights(options: {
    country?: string;
    industry?: string;
    page?: number;
    pageSize?: number;
  }): Observable<JobInsightsPaginatedResponse> {
    const { country, industry, page = 1, pageSize = 5 } = options;
    let params = new HttpParams().set('page', page).set('page_size', pageSize);
    if (country) {
      params = params.set('country', country);
    }
    if (industry) {
      params = params.set('industry', industry);
    }
    const url = `${this.API_BASE_URL}/users/job_role_insights?${params.toString()}`;
    return this._get(url).pipe(
      map((res) => this._handleResponse(res)),
      catchError((res) => this._handleError(res)),
    );
  }

  loadJobInsightsForCredential(
    credentialId: number,
    country?: string,
    industry?: string,
  ): Observable<CredentialJobInsights> {
    let url = `${this.CREDENTIAL_NET_API_BASE_URL}/credentials/${credentialId}/job_insights`;
    if (country) {
      const params = new HttpParams({ fromObject: { country, industry } });
      url = `${url}?${params}`;
    }

    return this._get(url).pipe(
      map((res) => this._handleResponse(res)),
      catchError((res) => this._handleError(res)),
    );
  }

  updateRelevance(id: number, status: JobRolePreferenceStatus): Observable<void> {
    const body: JobRolePreferenceRequest = {
      job_role_preference: {
        job_title_id: id,
        status,
      },
    };
    return this._post(`${this.API_BASE_URL}/job_role_preferences`, body).pipe(
      map(() => void 0),
      catchError((res) => this._handleError(res)),
    );
  }

  loadSuggestedJobRoles(): Observable<JobInsight[]> {
    const url = `${this.API_BASE_URL}/users/job_role_insights`;
    return this._get(url).pipe(
      map((res) => this._handleResponse(res, 'job_roles')),
      catchError((res) => this._handleError(res)),
    );
  }

  searchJobTitles(query: string): Observable<JobTitle[]> {
    const params = new HttpParams().set('query', query);
    const url = `${this.INTERNAL_API_BASE_URL}/job_titles?${params.toString()}`;
    return this._get(url).pipe(
      map((res) => this._handleResponse(res, 'job_titles')),
      catchError((res) => this._handleError(res)),
    );
  }

  getCareerPreferences(): Observable<CareerPreference | null> {
    return this._get(`${this.API_BASE_URL}/career_preferences`).pipe(
      map((res) => this._handleResponse(res, 'career_preference')),
      catchError((res) => {
        // This endpoint returns 404 when there are no saved career preferences
        if (res.status === 404) {
          return of(null);
        }
        return this._handleError(res);
      }),
    );
  }

  loadSuggestedGroups(
    currentJobTitleId: number,
    targetJobTitleId?: number,
  ): Observable<SuggestedGroup[]> {
    return this._post(`${this.API_BASE_URL}/users/suggested_groups`, {
      current_job_title_id: currentJobTitleId,
      target_job_title_id: targetJobTitleId,
    }).pipe(
      map((res) => this._handleResponse(res, 'suggested_groups')),
      catchError((res) => this._handleError(res)),
    );
  }

  trackJobTitleClick(viewId: number): Observable<void> {
    return this._post(`${this.API_BASE_URL}/job_title_analytics/${viewId}/track_click`, {}).pipe(
      map((res) => this._handleResponse(res)),
      catchError((res) => this._handleError(res)),
    );
  }

  updateCareerPreferences(preferences: {
    career_goal?: string;
    current_job_title_id?: number;
    target_job_title_id?: number;
    country_code: string;
    industry: string;
  }): Observable<CareerPreference> {
    const body: CareerPreferenceRequest = {
      career_preference: {
        career_goal: preferences.career_goal,
        current_job_title_id: preferences.current_job_title_id,
        target_job_title_id: preferences.target_job_title_id,
        country_code: preferences.country_code,
        industry: preferences.industry,
      },
    };
    return this._post(`${this.API_BASE_URL}/career_preferences`, body).pipe(
      map((res) => this._handleResponse(res, 'career_preference')),
      catchError((res) => this._handleError(res)),
    );
  }
}
